/**
 * rankAnim — local snapshot store powering the "joy/sadness" animations.
 *
 * We persist the value the user LAST SAW (per league, per surface) in
 * localStorage, then on the next visit we animate from that baseline to
 * the current value (count-up + green/red flash) and re-order leaderboard
 * rows from their previous positions to the new ones (the classic
 * leaderboard "shuffle"). Everything is per-device + best-effort: if
 * storage is unavailable we simply skip the animation and show the value.
 */
const PREFIX = 'fws:anim:v1:';

export function readSnap(key) {
  try {
    const raw = localStorage.getItem(PREFIX + key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function writeSnap(key, data) {
  try {
    localStorage.setItem(PREFIX + key, JSON.stringify(data));
  } catch {
    /* private mode / quota — animations gracefully no-op next time */
  }
}

/**
 * Given the current leaderboard rows and a snapshot key, returns:
 *   { deltas: {teamId: prevRank-currRank}, prevRanks, prevPoints, anyMoved }
 * and PERSISTS the new snapshot for next time. `deltas[id] > 0` => moved UP.
 */
export function computeLeaderboardDeltas(rows, snapKey) {
  const snap = readSnap(snapKey);
  const prevRanks = snap?.ranks || null;
  const prevPoints = snap?.points || null;

  const deltas = {};
  let anyMoved = false;
  if (prevRanks) {
    for (const t of rows) {
      const pr = prevRanks[t.id];
      if (pr != null && t.rank != null) {
        deltas[t.id] = pr - t.rank;
        if (deltas[t.id] !== 0) anyMoved = true;
      }
    }
  }

  // Persist the new baseline.
  const ranks = {};
  const points = {};
  for (const t of rows) {
    if (t.id == null) continue;
    ranks[t.id] = t.rank;
    points[t.id] = t.net_worth || 0;
  }
  writeSnap(snapKey, { ranks, points });

  return { deltas, prevRanks, prevPoints, anyMoved };
}
